import {
  Button,
  Checkbox,
  Label,
  Skeleton,
  Switch,
  ToastAction,
  useToast,
} from '@folio/components';
import {
  type InfiniteData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type {
  EmailThreadControllerListThreadsQueryParams,
  GetEmailThreadOutput,
  ListEmailThreadsOutput,
} from 'korint-sdk';
import {
  emailAccountControllerListMineQueryOptions,
  emailThreadControllerGetThreadQueryKey,
  emailThreadControllerGetThreadQueryOptions,
  emailThreadControllerListThreads,
  emailThreadControllerListThreadsQueryKey,
  emailThreadControllerSetThreadsArchived,
  generalInboxControllerGetSummaryQueryKey,
  generalInboxControllerGetSummaryQueryOptions,
} from 'korint-sdk';
import { Archive, ArchiveRestore, Inbox, Loader2, Plus, X } from 'lucide-react';
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { AttachSelectionButton } from './AttachSelectionButton';
import {
  EmailAccountsDrawer,
  type EmailAccountsDrawerView,
} from './EmailAccountsDrawer/EmailAccountsDrawer';
import { MailboxFilter } from './MailboxFilter';
import { SelectionAction } from './SelectionAction';
import { keepOpenedThread, type PinnedThread } from './openedThread';
import { ThreadCard } from './ThreadCard';
import { toggleSelection } from './threadHelpers';
import { ThreadReadingPane } from './ThreadReadingPane';
import { ToggleGroup } from './ToggleGroup';
import { Center, Group, Stack } from '@folio/components/ui/flex';
import { Text, Title } from '@folio/components/ui/text';

const PAGE_SIZE = 50;
// Mirrors the backend @ArrayMaxSize on the batch thread endpoints
const MAX_BATCH_THREADS = 500;

type ArchiveRequest = { threadIds: string[]; archived: boolean; emailIds?: string[] };
type Selection = { view: string; ids: ReadonlySet<string>; anchorId: string | null };

type Tab = 'to_attach' | 'attached' | 'archived';

const TABS: Tab[] = ['to_attach', 'attached', 'archived'];
const TAB_FILTERS = {
  to_attach: { mode: 'to_process' },
  attached: { mode: 'processed', processedFilter: 'linked' },
  archived: { mode: 'processed', processedFilter: 'archived' },
} as const satisfies Record<
  Tab,
  Pick<EmailThreadControllerListThreadsQueryParams, 'mode' | 'processedFilter'>
>;

export function GeneralInboxModule() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<Tab>('to_attach');
  const [unreadOnly, setUnreadOnly] = useState(false);
  const unreadSwitchId = useId();
  // Null means every listable mailbox, disconnected accounts' history included.
  const [mailboxFilter, setMailboxFilter] = useState<string | null>(null);
  const [drawerView, setDrawerView] = useState<EmailAccountsDrawerView | null>(null);

  const { data: mailboxes } = useQuery(emailAccountControllerListMineQueryOptions());
  const accounts = mailboxes?.accounts ?? [];
  // A disconnected mailbox drops out of the filter
  const selectedAccountId = accounts.some(({ id }) => id === mailboxFilter) ? mailboxFilter : null;
  const { data: summary } = useQuery(generalInboxControllerGetSummaryQueryOptions());

  const params: EmailThreadControllerListThreadsQueryParams = {
    limit: PAGE_SIZE,
    ...TAB_FILTERS[tab],
    ...(unreadOnly && { unread: true }),
    ...(selectedAccountId && { accountIds: [selectedAccountId] }),
  };
  const isDefaultView = tab === 'to_attach' && !unreadOnly && !selectedAccountId;

  const {
    data,
    isPending,
    isError,
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey: emailThreadControllerListThreadsQueryKey(params),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => emailThreadControllerListThreads({ ...params, cursor: pageParam }),
    getNextPageParam: lastPage => lastPage.nextCursor ?? undefined,
    refetchInterval: query =>
      query.state.data?.pages.some(({ threads }) =>
        threads.some(({ analysisPending }) => analysisPending),
      )
        ? 3000
        : false,
  });

  const sentinelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const sentinel = sentinelRef.current;
    // A failed page waits for the retry button: the sentinel stays in view and would refetch forever.
    if (!sentinel || !hasNextPage || isFetchNextPageError) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry?.isIntersecting && !isFetchingNextPage) fetchNextPage();
    });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, isFetchNextPageError, fetchNextPage]);

  const [searchParams, setSearchParams] = useSearchParams();
  const openedThreadId = searchParams.get('thread');

  const fetchedThreads = data?.pages.flatMap(page => page.threads) ?? [];
  const view = JSON.stringify(params);
  const pinnedRef = useRef<PinnedThread | null>(null);
  const openedIndex = fetchedThreads.findIndex(({ id }) => id === openedThreadId);
  const openedThread = fetchedThreads[openedIndex];
  if (openedThread) pinnedRef.current = { view, index: openedIndex, thread: openedThread };
  else if (pinnedRef.current?.thread.id !== openedThreadId) pinnedRef.current = null;
  const { data: openedDetail } = useQuery({
    ...emailThreadControllerGetThreadQueryOptions(openedThreadId ?? ''),
    enabled: false,
  });
  const threads = keepOpenedThread({
    threads: fetchedThreads,
    pinned: pinnedRef.current,
    view,
    detail: openedDetail,
  });
  // Tied to the view it was made in: switching tab or filter drops it
  const [selection, setSelection] = useState<Selection>({ view, ids: new Set(), anchorId: null });
  const checkedIds = selection.view === view ? selection.ids : new Set<string>();
  const checkedThreads = threads.filter(({ id }) => checkedIds.has(id));
  const isEveryThreadChecked = threads.length > 0 && checkedThreads.length === threads.length;
  const exceedsBatchLimit = checkedThreads.length > MAX_BATCH_THREADS;
  const clearSelection = () => setSelection({ view, ids: new Set(), anchorId: null });
  const checkThread = (id: string, extendsRange: boolean) =>
    setSelection(previous => {
      const isSameView = previous.view === view;
      return {
        view,
        ids: toggleSelection({
          orderedIds: threads.map(thread => thread.id),
          selectedIds: isSameView ? previous.ids : new Set(),
          anchorId: isSameView ? previous.anchorId : null,
          id,
          extendsRange,
        }),
        anchorId: id,
      };
    });
  const checkEveryThread = () =>
    setSelection({
      view,
      ids: new Set(isEveryThreadChecked ? [] : threads.map(({ id }) => id)),
      anchorId: null,
    });
  // Read by async callbacks, which would otherwise see the selection of their render
  const openedThreadIdRef = useRef(openedThreadId);
  openedThreadIdRef.current = openedThreadId;
  const targetEmailId = searchParams.get('email');
  // Folded thread id, not a boolean: back/forward and deep links switch threads without a click
  const [foldedThreadId, setFoldedThreadId] = useState<string | null>(null);
  // Bumped on every email click so re-clicking the same email scrolls and highlights it again
  const [emailSelectionCount, setEmailSelectionCount] = useState(0);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  useEffect(() => {
    const outcome = searchParams.get('emailAccountConnected');
    if (!outcome) return;
    if (outcome === 'success') {
      toast({
        title: t('connectedEmailAccounts.toast.successTitle'),
        description: t('connectedEmailAccounts.toast.successDescription'),
      });
    } else if (outcome === 'already-connected') {
      toast({
        variant: 'warning',
        title: t('connectedEmailAccounts.toast.alreadyConnectedTitle'),
        description: t('connectedEmailAccounts.toast.alreadyConnectedDescription'),
      });
    } else {
      toast({
        variant: 'destructive',
        title: t('connectedEmailAccounts.toast.errorTitle'),
        description: t('connectedEmailAccounts.toast.errorDescription'),
      });
    }
    setSearchParams(
      previous => {
        const next = new URLSearchParams(previous);
        next.delete('emailAccountConnected');
        return next;
      },
      { replace: true },
    );
  }, [searchParams, setSearchParams, t, toast]);

  const openThread = (threadId: string, emailId?: string) =>
    setSearchParams(previous => {
      const next = new URLSearchParams(previous);
      next.set('thread', threadId);
      if (emailId) next.set('email', emailId);
      else next.delete('email');
      return next;
    });

  const toggleThread = (threadId: string) => {
    if (threadId === openedThreadId) {
      setFoldedThreadId(folded => (folded === threadId ? null : threadId));
      return;
    }
    setFoldedThreadId(null);
    openThread(threadId);
  };

  // Unknown flip for a thread outside the loaded pages: refresh the badge too
  const markThreadRead = useCallback(
    (threadId: string) => {
      let found = false;
      let flipped = false;
      queryClient.setQueriesData<InfiniteData<ListEmailThreadsOutput>>(
        { queryKey: emailThreadControllerListThreadsQueryKey() },
        previous =>
          previous && {
            ...previous,
            pages: previous.pages.map(page => ({
              ...page,
              threads: page.threads.map(thread => {
                if (thread.id !== threadId) return thread;
                found = true;
                if (!thread.isUnread) return thread;
                flipped = true;
                return { ...thread, isUnread: false };
              }),
            })),
          },
      );
      if (flipped || !found) {
        queryClient.invalidateQueries({ queryKey: generalInboxControllerGetSummaryQueryKey() });
      }
    },
    [queryClient],
  );

  const { mutateAsync: setArchived } = useMutation({
    mutationFn: ({ isListUpToDate: _, ...body }: ArchiveRequest & { isListUpToDate?: boolean }) =>
      emailThreadControllerSetThreadsArchived(body),
    onSettled: async (_, error, { threadIds, isListUpToDate }) => {
      const listQueryKey = emailThreadControllerListThreadsQueryKey();
      // Skipped when the optimistic removal shows it; a failed rollback snapshot may be stale
      const refetchesList = !isListUpToDate || error !== null;
      // A first fetch begun before the write absorbs refetches and answers stale
      if (refetchesList) await queryClient.cancelQueries({ queryKey: listQueryKey });
      queryClient.invalidateQueries({
        queryKey: listQueryKey,
        refetchType: refetchesList ? 'active' : 'none',
      });
      for (const threadId of threadIds) {
        queryClient.invalidateQueries({
          queryKey: emailThreadControllerGetThreadQueryKey(threadId),
        });
      }
      queryClient.invalidateQueries({ queryKey: generalInboxControllerGetSummaryQueryKey() });
    },
  });

  // Undo is offered only once the archive is stored, so the two writes cannot land out of order
  const setThreadsArchived = ({ threadIds, archived, emailIds }: ArchiveRequest) => {
    // Removed up front only when certain: the thread's other emails can't keep it in the view
    const leavesView = (threadId: string) => {
      const otherEmails = emailIds
        ? queryClient
            .getQueryData<GetEmailThreadOutput>(emailThreadControllerGetThreadQueryKey(threadId))
            ?.emails.filter(({ id }) => !emailIds.includes(id))
        : [];
      return (
        otherEmails !== undefined &&
        (archived
          ? tab === 'to_attach' && otherEmails.every(({ isToProcess }) => !isToProcess)
          : tab === 'archived' && otherEmails.every(({ isArchived }) => !isArchived))
      );
    };
    const listQueryKey = emailThreadControllerListThreadsQueryKey(params);
    const previous = queryClient.getQueryData<InfiniteData<ListEmailThreadsOutput>>(listQueryKey);
    // No cached list (still loading): nothing to remove, so the list must refetch
    const removesFromList = threadIds.every(leavesView) && previous !== undefined;
    if (removesFromList) {
      queryClient.setQueryData(listQueryKey, {
        ...previous,
        pages: previous.pages.map(page => ({
          ...page,
          threads: page.threads.filter(({ id }) => !threadIds.includes(id)),
        })),
      });
    }
    const movesPane =
      removesFromList && openedThreadId !== null && threadIds.includes(openedThreadId);
    const index = threads.findIndex(({ id }) => id === openedThreadId);
    const isKept = ({ id }: { id: string }) => !threadIds.includes(id);
    const nextThreadId =
      (threads.slice(index + 1).find(isKept) ?? threads.slice(0, index).reverse().find(isKept))
        ?.id ?? null;
    const selectThread = (selectedThreadId: string | null, emailId: string | null) =>
      setSearchParams(previousParams => {
        const nextParams = new URLSearchParams(previousParams);
        if (selectedThreadId) nextParams.set('thread', selectedThreadId);
        else nextParams.delete('thread');
        if (emailId) nextParams.set('email', emailId);
        else nextParams.delete('email');
        return nextParams;
      });
    if (movesPane) selectThread(nextThreadId, null);
    const scope = emailIds ? 'email' : 'thread';
    setArchived({
      threadIds,
      archived,
      isListUpToDate: removesFromList,
      ...(emailIds && { emailIds }),
    }).then(
      ({ emailIds: changedIds }) => {
        if (changedIds.length === 0) return;
        toast({
          description: t(`generalInbox.archive.${scope}.${archived ? 'archived' : 'unarchived'}`, {
            count: threadIds.length,
          }),
          action: (
            <ToastAction
              altText={t('generalInbox.archive.undo')}
              onClick={() =>
                setArchived({ threadIds, archived: !archived, emailIds: changedIds }).catch(
                  () => {},
                )
              }
            >
              {t('generalInbox.archive.undo')}
            </ToastAction>
          ),
        });
      },
      () => {
        if (removesFromList) queryClient.setQueryData(listQueryKey, previous);
        // Only while the pane still shows the thread it moved to: the user may have navigated since
        if (movesPane && openedThreadIdRef.current === nextThreadId) {
          selectThread(openedThreadId, targetEmailId);
        }
      },
    );
  };

  const setCheckedThreadsArchived = () => {
    setThreadsArchived({
      threadIds: checkedThreads.map(({ id }) => id),
      archived: tab !== 'archived',
    });
    clearSelection();
  };

  const drawer = <EmailAccountsDrawer view={drawerView} onClose={() => setDrawerView(null)} />;

  if (mailboxes?.accounts.length === 0) {
    return (
      <>
        <Stack
          align="center"
          justify="center"
          gap={3}
          className="h-[calc(100vh-14rem)] min-h-96 rounded-lg border bg-card p-8 text-center"
        >
          <span className="flex size-12 items-center justify-center rounded-xl bg-primary/10">
            <Inbox className="size-6 text-primary" aria-hidden />
          </span>
          <Title order={2} size="lg">
            {t('generalInbox.noAccount.title')}
          </Title>
          <Text size="sm" tone="muted" className="max-w-sm">
            {t('generalInbox.noAccount.description')}
          </Text>
          <Button
            size="sm"
            leftIcon={<Plus className="size-4" />}
            onClick={() => setDrawerView('connect')}
          >
            {t('connectedEmailAccounts.connect')}
          </Button>
        </Stack>
        {drawer}
      </>
    );
  }

  return (
    <Stack>
      <Group wrap justify="between" gap={2}>
        <ToggleGroup
          label={t('generalInbox.filters.tab.label')}
          options={TABS}
          value={tab}
          onChange={setTab}
          optionLabel={option => t(`generalInbox.filters.tab.${option}`)}
        />
        <Group gap={2}>
          {accounts.length > 0 && (
            <MailboxFilter
              accounts={accounts}
              showShared={mailboxes?.canManageSharedAccounts ?? false}
              unreadCountByAccount={
                new Map(
                  summary?.unreadThreadCountByAccount.map(
                    ({ accountId, unreadThreadCount }) => [accountId, unreadThreadCount] as const,
                  ),
                )
              }
              totalUnreadCount={summary?.unreadThreadCount ?? 0}
              selectedId={selectedAccountId}
              onSelect={setMailboxFilter}
              onConnect={() => setDrawerView('connect')}
              onManage={() => setDrawerView('accounts')}
            />
          )}
          <Group gap={2}>
            <Switch id={unreadSwitchId} checked={unreadOnly} onCheckedChange={setUnreadOnly} />
            <Label htmlFor={unreadSwitchId} className="text-xs font-medium whitespace-nowrap">
              {t('generalInbox.filters.unread')}
            </Label>
          </Group>
        </Group>
      </Group>

      <div className="grid h-[calc(100vh-14rem)] min-h-96 grid-cols-[minmax(20rem,26rem)_1fr] overflow-hidden rounded-lg border bg-card">
        <div className="overflow-y-auto border-r">
          {isError && !data ? (
            <Text size="sm" tone="destructive" className="p-4">
              {t('generalInbox.error')}
            </Text>
          ) : isPending ? (
            <Stack gap={2} className="p-4">
              {Array.from({ length: 5 }, (_, index) => (
                <Skeleton key={index} className="h-20 w-full" />
              ))}
            </Stack>
          ) : threads.length === 0 ? (
            <div className="px-4 py-10 text-center">
              <Text weight="semibold">
                {isDefaultView
                  ? t('generalInbox.empty.title')
                  : t('generalInbox.emptyFiltered.title')}
              </Text>
              <Text size="sm" tone="muted" className="mt-1">
                {isDefaultView
                  ? t('generalInbox.empty.description')
                  : t('generalInbox.emptyFiltered.description')}
              </Text>
            </div>
          ) : (
            <>
              <Group
                gap={2}
                className="sticky top-0 z-20 h-11 border-b bg-card px-4"
                data-testid="general-inbox-selection-bar"
              >
                <Checkbox
                  checked={isEveryThreadChecked || (checkedThreads.length > 0 && 'indeterminate')}
                  aria-label={t('generalInbox.selection.selectAll')}
                  data-testid="general-inbox-select-all"
                  onCheckedChange={checkEveryThread}
                />
                {checkedThreads.length === 0 ? (
                  <Text inline size="xs" tone="muted">
                    {t('generalInbox.selection.selectAll')}
                  </Text>
                ) : (
                  <>
                    <Text
                      inline
                      size="xs"
                      weight="medium"
                      tone={exceedsBatchLimit ? 'destructive' : undefined}
                      className="flex-1 whitespace-nowrap"
                    >
                      {exceedsBatchLimit
                        ? t('generalInbox.selection.tooMany', {
                            count: checkedThreads.length,
                            max: MAX_BATCH_THREADS,
                          })
                        : t('generalInbox.selection.count', { count: checkedThreads.length })}
                    </Text>
                    {tab === 'to_attach' && (
                      <AttachSelectionButton
                        threads={checkedThreads}
                        disabled={exceedsBatchLimit}
                        onSubmit={clearSelection}
                      />
                    )}
                    <SelectionAction
                      label={t(
                        `generalInbox.selection.${tab === 'archived' ? 'unarchive' : 'archive'}`,
                      )}
                      icon={tab === 'archived' ? ArchiveRestore : Archive}
                      testId="general-inbox-archive-selection"
                      disabled={exceedsBatchLimit}
                      onClick={setCheckedThreadsArchived}
                    />
                    <SelectionAction
                      label={t('generalInbox.selection.clear')}
                      icon={X}
                      testId="general-inbox-clear-selection"
                      onClick={clearSelection}
                    />
                  </>
                )}
              </Group>
              <ul className="py-1">
                {threads.map(thread => (
                  <ThreadCard
                    key={thread.id}
                    thread={thread}
                    isSelected={thread.id === openedThreadId}
                    selectedEmailId={targetEmailId}
                    isExpanded={
                      thread.id === openedThreadId &&
                      thread.id !== foldedThreadId &&
                      thread.emailCount > 1
                    }
                    onToggle={() => toggleThread(thread.id)}
                    onSelectEmail={emailId => {
                      openThread(thread.id, emailId);
                      setEmailSelectionCount(count => count + 1);
                    }}
                    isChecked={checkedIds.has(thread.id)}
                    onCheck={extendsRange => checkThread(thread.id, extendsRange)}
                    onSetArchived={archived =>
                      setThreadsArchived({ threadIds: [thread.id], archived })
                    }
                  />
                ))}
              </ul>
              <Center ref={sentinelRef} className="h-8">
                {isFetchingNextPage ? (
                  <Text inline size="xs" tone="muted" className="inline-flex items-center gap-2">
                    <Loader2 className="size-3 animate-spin" />
                    {t('generalInbox.loadingMore')}
                  </Text>
                ) : (
                  isFetchNextPageError && (
                    <Text
                      inline
                      size="xs"
                      tone="destructive"
                      className="inline-flex items-center gap-2"
                    >
                      {t('generalInbox.loadMoreError')}
                      <Button variant="link" size="sm" onClick={() => fetchNextPage()}>
                        {t('generalInbox.retry')}
                      </Button>
                    </Text>
                  )
                )}
              </Center>
            </>
          )}
        </div>

        {openedThreadId ? (
          <ThreadReadingPane
            key={openedThreadId}
            threadId={openedThreadId}
            emailId={targetEmailId}
            emailSelectionCount={emailSelectionCount}
            onRead={markThreadRead}
            onSetArchived={(emailId, archived) =>
              setThreadsArchived({ threadIds: [openedThreadId], archived, emailIds: [emailId] })
            }
          />
        ) : (
          <Stack align="center" justify="center" gap={2} className="text-muted-foreground">
            <Inbox className="size-8" aria-hidden />
            <Text size="sm">{t('generalInbox.selectThread')}</Text>
          </Stack>
        )}
      </div>
      {drawer}
    </Stack>
  );
}
